"use client"

import React, { useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface InteractiveGradientButtonProps
  extends React.ComponentProps<typeof Button> {
  children: React.ReactNode
}

export function InteractiveGradientButton({
  children,
  className,
  ...props
}: InteractiveGradientButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const button = buttonRef.current
    if (!button) return

    let isHovered = false
    let baseX = 50
    let baseY = 50

    // Глобальное движение мыши по всей странице
    const handleGlobalMouseMove = (e: MouseEvent) => {
      // Базовый параллакс от движения по всей странице
      const pageX = (e.clientX / window.innerWidth) * 100
      const pageY = (e.clientY / window.innerHeight) * 100
      
      baseX = 30 + (pageX * 0.4) // Диапазон от 30% до 70%
      baseY = 30 + (pageY * 0.4) // Диапазон от 30% до 70%
      
      if (!isHovered) {
        button.style.backgroundPosition = `${baseX}% ${baseY}%`
      }
    }

    // Локальное движение мыши по кнопке (дополнительный эффект)
    const handleButtonMouseMove = (e: MouseEvent) => {
      if (!isHovered) return
      
      const rect = button.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      
      // Дополнительное смещение при ховере
      const xPercent = (x / rect.width) * 100
      const yPercent = (y / rect.height) * 100
      
      const hoverX = baseX + (xPercent - 50) * 0.3 // Дополнительные 30%
      const hoverY = baseY + (yPercent - 50) * 0.3
      
      button.style.backgroundPosition = `${hoverX}% ${hoverY}%`
    }

    const handleMouseEnter = () => {
      isHovered = true
    }

    const handleMouseLeave = () => {
      isHovered = false
      // Возвращаемся к базовому параллаксу
      button.style.backgroundPosition = `${baseX}% ${baseY}%`
    }

    // Подписываемся на события
    document.addEventListener("mousemove", handleGlobalMouseMove)
    button.addEventListener("mousemove", handleButtonMouseMove)
    button.addEventListener("mouseenter", handleMouseEnter)
    button.addEventListener("mouseleave", handleMouseLeave)

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove)
      button.removeEventListener("mousemove", handleButtonMouseMove)
      button.removeEventListener("mouseenter", handleMouseEnter)
      button.removeEventListener("mouseleave", handleMouseLeave)
    }
  }, [])

  return (
    <Button
      ref={buttonRef}
      className={cn(
        "gradient-button text-white border-0 transition-transform duration-200 ease-out",
        className
      )}
      {...props}
    >
      {children}
    </Button>
  )
} 